<template>
  <div
    class="relative flex flex-col"
    :class="colSpan"
  >
    <h3
      v-if="field?.display"
      class="typo-h-5 w-full"
    >
      {{ field.display }}
    </h3>
    <p v-if="instructions || field.instructions" class="mt-1">
      {{ instructions || field.instructions }}
    </p>

    <ul
      class="flex flex-1 flex-col flex-wrap space-y-2"
      :class="{ 'mt-2': field?.display || instructions || field.instructions }"
    >
      <li v-for="(option, index) in computedOptions" :key="index">
        <label
          class="
            checkbox-container relative block cursor-pointer select-none
            hyphens-auto pl-8
          "
          :for="`${idPrefix}${option.value}`"
        >
          <span
            class="link negative"
            v-html="option.label"
          />

          <Field
            :id="`${idPrefix}${option.value}`"
            :value="option.value"
            :name="name || handle || field.handle"
            :label="label || option.label || field.label || field.display"
            :disabled="disabled"
            :rules="rulesAsString"
            :type="type"
            :model-value="modelValue"
            class="
              peer absolute left-0 z-0 translate-y-0.5 cursor-pointer opacity-0
            "
            @update:model-value="$emit('update:model-value', $event)"
          />

          <span
            class="
              checkbox-checkmark absolute inset-x-0 top-0 mt-1 block
              size-[1.25em] shrink-0 translate-y-[-0.1em] rounded-sm
              border-[0.125rem] border-black duration-100 ease-in-out
              peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2
            "
            :class="[
              containerBackground === 'white' ?
                `border-black` :
                `border-white bg-white`,
            ]"
          />
        </label>
      </li>
    </ul>

    <!-- ERROR -->
    <TransitionExpand
      class="transition-all duration-300 ease-in-out"
      :open="!!error"
    >
      <p
        v-if="error"
        class="hyphens-auto pt-1 text-sm"
        :class="[
          {
            'text-red-500': containerBackground === 'white',
            'text-white': containerBackground === 'colored',
          },
        ]"
      >
        {{ error }}
      </p>
    </TransitionExpand>
  </div>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
import { Field } from 'vee-validate'

defineEmits(['update:model-value'])

const props = defineProps({
  options: {
    type: [Array, Object],
    default: () => [],
  },

  handle: {
    type: String,
    default: null,
  },

  type: {
    type: String,
    default: 'checkbox',
  },

  modelValue: {
    type: [String, Number, Boolean, Array],
    default: () => [],
  },

  field: {
    type: Object,
    default: null,
  },

  width: {
    type: [Number, String],
    default: null,
  },

  rules: {
    type: [Array, String],
    default: '',
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  label: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: null,
  },

  instructions: {
    type: String,
    default: null,
  },

  error: {
    type: String,
    default: null,
  },

  idPrefix: {
    type: String,
    default: '',
  },

  containerBackground: {
    type: String,
    default: 'white',
  },
})

const rulesAsString = useFilterVeeValidateRules(props.rules)

const colSpan = computed(() => {
  const width = parseInt(props.width) || props.field?.width || 100

  switch (width) {
    case 25:
      return 'lg:col-span-3 col-span-12'
    case 33:
      return 'lg:col-span-4 col-span-12'
    case 50:
      return 'lg:col-span-6 col-span-12'
    case 66:
      return 'lg:col-span-8 col-span-12'
    default:
      return 'lg:col-span-12 col-span-12'
  }
})

const computedOptions = computed(() => {
  if (Array.isArray(props.options)) {
    return props.options.map((option) => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        }
      }

      return {
        label: option.value,
        value: option.key,
      }
    })
  }

  return Object.keys(props.options).map(key => ({
    label: props.options[key],
    value: key,
  }))
})
</script>
